import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    inject,
    input,
    type OnChanges,
    type OnDestroy,
    type OnInit,
    PLATFORM_ID,
    Renderer2,
    RendererStyleFlags2,
    viewChild
} from '@angular/core';
import { type IProductDataV3 } from '@df/catalog/product/product';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { Wishlist } from '@df/session/api/wishlist/wishlist';
import { UiComponent } from '@df/ui/ui.component';
import { gsap } from 'gsap';
import { MotionPathPlugin } from 'gsap/MotionPathPlugin';
import { take } from 'rxjs/internal/operators/take';
import { type ProductDecorator } from '../../../../../../catalog/model/product/product.decorator';
import { ProductConfigurationPlugin } from '../../../../../../module/product-configuration/plugins/product-configuration-plugin';
import { ActionComponent } from '../../../../../../ui/api/action.component';
import { ResultComponent } from '../../../../../../ui/api/result.component';
import { ProductLayoutComponent } from '../../product-layout.component';
import { ProductLayoutWishlistService, WishlistState } from '../product-layout-wishlist.service';

@Component({
    selector: 'product-layout-wishlist-toggle',
    templateUrl: './product-layout-wishlist-toggle.component.html',
    styleUrl: './product-layout-wishlist-toggle.component.scss',
    standalone: true,
    imports: [ActionComponent, CommonModule, ResultComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductLayoutWishlistToggleComponent extends UiComponent implements OnInit, OnChanges, OnDestroy {
    readonly productViewLayout = input.required<ProductLayoutComponent>();

    readonly buttonRef = viewChild('buttonRef', { read: ElementRef });
    readonly iconRef = viewChild('iconRef', { read: ElementRef });
    readonly iconHeartLeftRef = viewChild('iconHeartLeftRef', { read: ElementRef });
    readonly iconHeartRightRef = viewChild('iconHeartRightRef', { read: ElementRef });

    remove: () => void;
    toggle: () => void;

    protected readonly productLayoutWishlistService = inject(ProductLayoutWishlistService);
    private readonly productConfiguration = inject(ProductConfiguration);
    private readonly productConfigurationPlugin = inject(ProductConfigurationPlugin);
    private readonly renderer = inject(Renderer2);
    private readonly isPlatformBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    private readonly wishlist = inject(Wishlist);

    private tween?: gsap.core.Timeline;
    private tweenLeft?: gsap.core.Timeline;
    private tweenRight?: gsap.core.Timeline;
    private resetTimeout?: number;

    constructor() {
        super();

        this.remove = this.timeoutService.throttle(() => this._remove(), 300);
        this.toggle = this.timeoutService.throttle(() => this._toggle(), 300);

        this.subscriptions.add(this.productLayoutWishlistService.wishlistStatus.subscribe(() => this.detectChanges()));
    }

    get product(): ProductDecorator<IProductDataV3> {
        return this.productConfiguration.product$.value as ProductDecorator<IProductDataV3>;
    }

    /**
     * Checks if item is in wishlist already
     */
    get inWishlist(): boolean {
        return (
            this.productLayoutWishlistService.state() === WishlistState.BeforeAdd ||
            this.hasAnyInWishlist ||
            (this.wishlist.getItemsByProduct(this.product.id) || []).length > 0
        );
    }

    get hasAnyInWishlist(): boolean {
        return !!this.product.parent.children.find(product => (this.wishlist.getItemsByProduct(product.id) || []).length > 0);
    }

    override ngOnInit() {
        this.registerGsapAnimation();

        super.ngOnInit();
    }

    override ngOnDestroy(): void {
        this.tween?.kill();
        this.tweenLeft?.kill();
        this.tweenRight?.kill();

        super.ngOnDestroy();
    }

    /**
     * Open modal
     */
    handleClick() {
        this.productLayoutWishlistService.wishlistStatus.busy = true;

        const missingAttrCode = this.productConfigurationPlugin.needSelectAttribute();

        if (!this.inWishlist) {
            if (missingAttrCode) {
                this.productLayoutWishlistService.state.set(WishlistState.BeforeAdd);
                this.productLayoutWishlistService.missingAttrCode.set(missingAttrCode);
                this.productLayoutWishlistService.callBack.set(() => {
                    this.toggle();
                });

                if (this.client.isS || this.client.isM) {
                    this.productViewLayout().openProductLayoutConfigurableOptionsModal();
                }
            } else {
                this.toggle();
            }
        } else {
            this.remove();
        }
    }

    /**
     * Add or remove from wishlist
     */
    private _toggle() {
        this.productLayoutWishlistService.toggle(this.product, () => {
            this.productLayoutWishlistService.state.set(WishlistState.AddSuccess);

            clearTimeout(this.resetTimeout);

            this.resetTimeout = this.timeoutService.setTimeout(() => {
                this.productLayoutWishlistService.reset();
            }, 5000);

            if (this.client.isS || this.client.isM) {
                this.productViewLayout().closeProductLayoutConfigurableOptionsModal();
            }
        });
    }

    /**
     * Remove items from wishlist
     */
    private _remove() {
        this.productLayoutWishlistService.remove(this.product, () => {
            this.onRemove();
        });
    }

    private registerGsapAnimation() {
        if (this.isPlatformBrowser) {
            gsap.registerPlugin(MotionPathPlugin);
        }
    }

    private onRemove() {
        if (this.isPlatformBrowser) {
            const button = this.buttonRef()?.nativeElement;
            const icon = this.iconRef()?.nativeElement;
            const iconRect = icon?.getBoundingClientRect();
            const iconHeartLeft = this.iconHeartLeftRef()?.nativeElement;
            const iconHeartRight = this.iconHeartRightRef()?.nativeElement;

            this.tween?.kill();
            this.tweenLeft?.kill();
            this.tweenRight?.kill();

            this.tween = gsap.timeline();
            this.tweenLeft = gsap.timeline();
            this.tweenRight = gsap.timeline();

            const pathLeft = [
                { x: 0, y: 0 },
                { x: -3, y: -30 },
                { x: -10, y: 50 },
                { x: 1, y: 100 }
            ];
            const pathRight = [
                { x: 0, y: 0 },
                { x: 5, y: -50 },
                { x: 10, y: 50 },
                { x: 3, y: 100 }
            ];

            if (iconHeartLeft) {
                this.renderer.removeClass(iconHeartLeft, 'ng-hide');
            }

            if (iconHeartRight) {
                this.renderer.removeClass(iconHeartRight, 'ng-hide');
            }

            if (button) {
                this.renderer.setStyle(button, '--wishlist-icon-x', `${iconRect.x}px`, RendererStyleFlags2.DashCase);
                this.renderer.setStyle(button, '--wishlist-icon-y', `${iconRect.y}px`, RendererStyleFlags2.DashCase);
            }

            this.tween
                .to(icon, {
                    duration: 0,
                    opacity: 0
                })
                .to(icon, {
                    delay: 1,
                    duration: 0.5,
                    opacity: 1
                });

            this.tweenLeft
                .to(iconHeartLeft, {
                    duration: 0,
                    scale: 1,
                    opacity: 1
                })
                .to(iconHeartLeft, {
                    duration: 8,
                    ease: 'back.out(4)',
                    rotation: '-30',
                    opacity: 0,
                    motionPath: {
                        path: pathLeft,
                        type: 'cubic'
                    }
                })
                .to(iconHeartLeft, {
                    duration: 0.5,
                    scale: 0
                })
                .then(() => {
                    this.renderer.addClass(iconHeartLeft, 'ng-hide');
                    this.tweenLeft?.progress(0).kill();
                });

            this.tweenRight
                .to(iconHeartRight, {
                    duration: 0,
                    scale: 1,
                    opacity: 1
                })
                .to(iconHeartRight, {
                    duration: 8,
                    ease: 'back.out(4)',
                    rotation: '40',
                    opacity: 0,
                    motionPath: {
                        path: pathRight,
                        type: 'cubic'
                    }
                })
                .to(iconHeartRight, {
                    duration: 0.5,
                    scale: 0
                })
                .then(() => {
                    this.renderer.addClass(iconHeartRight, 'ng-hide');
                    this.tweenRight?.progress(0).kill();
                });

            this.subscriptions.add(
                this.client.obs.scroll.pipe(take(1)).subscribe(() => {
                    this.tween?.progress(1);
                    this.tweenLeft?.progress(1);
                    this.tweenRight?.progress(1);
                })
            );
        }
    }
}
